"use client";

import Link from "next/link";
import { useState } from "react";
import { ChevronDown, Mail, MapPin, Phone, Shield } from "lucide-react";
import { megaMenuData } from "@/lib/navigation";
import { companyInfo } from "@/lib/companyInfo";
import Image from "next/image";

interface FooterSectionProps {
  title: string;
  items: { title: string; href: string }[];
  isOpen: boolean;
  onToggle: () => void;
}

function FooterSection({ title, items, isOpen, onToggle }: FooterSectionProps) {
  if (items.length === 0) return null;
  
  return (
    <div className="xl:border-none py-1.5 sm:py-0">
      {/* Mobile: Collapsible header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 sm:py-3 xl:py-0 xl:mb-5 xl:cursor-default"
        aria-expanded={isOpen}
      >
        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
          {title}
        </h3>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 xl:hidden transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out xl:!max-h-none xl:!opacity-100 ${
          isOpen ? "max-h-96 opacity-100 pb-5 sm:pb-4" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="space-y-2.5 pt-2.5 xl:pt-0">
          {items.map((item, index) => (
            <li key={index}>
              <Link
                href={item.href}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 block py-1"
              >
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function Footer() {
  const [openSections, setOpenSections] = useState({
    services: true,
    solutions: true,
    company: true,
    resources: true,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const servicesMenu = megaMenuData.find((menu) => menu.title === "Services");
  const solutionsMenu = megaMenuData.find((menu) => menu.title === "Solutions");
  const companyMenu = megaMenuData.find((menu) => menu.title === "Company");

  const servicesItems = servicesMenu?.sections?.[0]?.items ?? [];
  const businessSolutions =
    solutionsMenu?.sections?.find(
      (section) => section.title === "Solutions Overview"
    )?.items ?? [];
  const companyItems =
    companyMenu?.sections?.find((section) => section.title === "About Us")
      ?.items ?? [];
  const resourcesItems =
    companyMenu?.sections?.find((section) => section.title === "Resources")
      ?.items ?? [];

  return (
    <footer className="w-full text-gray-800 relative overflow-hidden bg-white border-t border-gray-200">
      {/* Subtle background pattern - toned down dots */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, gray 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }} />
      </div>
      
      {/* Grid-aligned container - matches Section px-4 sm:px-6 xl:px-4 xl:mx-[16.67%] */}
      <div className="relative px-4 sm:px-6 xl:px-4 xl:mx-[16.67%] pt-12 xl:pt-16 pb-6 sm:pb-8">
        {/* Brand + Description */}
        <div className="flex flex-col gap-6 sm:gap-7 mb-8 sm:mb-10 pb-6 sm:pb-8 border-b-0 sm:border-b sm:border-gray-200">
          {/* Logo and Badge Row */}
          <div className="flex items-center justify-between">
            <Image
              src="/assets/REPLENICO/logos/svgs/replenico-logo-horizontal-stacked-dark.svg"
              alt="Replenico"
              width={180}
              height={54}
              className="h-12 sm:h-14 w-auto opacity-90"
            />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-300 bg-white/50">
              <Shield className="w-4 h-4 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">UK Based</span>
            </div>
          </div>
          
          {/* Description */}
          <div>
            <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed max-w-4xl">
              Premium linen services and property support solutions for hospitality professionals. Elevating guest experiences across the UK.
            </p>
          </div>
          
          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 xl:gap-10 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0 border border-gray-200">
                <Mail className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email</p>
                <a href={companyInfo.emailLink} className="text-gray-700 hover:text-gray-900 transition-colors">
                  {companyInfo.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0 border border-gray-200">
                <Phone className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Phone</p>
                <a href={companyInfo.phoneLink} className="text-gray-700 hover:text-gray-900 transition-colors">
                  {companyInfo.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0 border border-gray-200">
                <MapPin className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Location</p>
                <p className="text-gray-700">{companyInfo.address.city}, {companyInfo.address.country}</p>
              </div>
            </div>
          </div>

        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-0 sm:gap-6 xl:gap-8 divide-y divide-gray-200 sm:divide-y-0 mb-8 sm:mb-10">
          <FooterSection
            title="Services"
            items={servicesItems}
            isOpen={openSections.services}
            onToggle={() => toggleSection("services")}
          />
          <FooterSection
            title="Solutions"
            items={businessSolutions}
            isOpen={openSections.solutions}
            onToggle={() => toggleSection("solutions")}
          />
          <FooterSection
            title="Company"
            items={companyItems}
            isOpen={openSections.company}
            onToggle={() => toggleSection("company")}
          />
          <FooterSection
            title="Resources"
            items={resourcesItems}
            isOpen={openSections.resources}
            onToggle={() => toggleSection("resources")}
          />
        </div>

      </div>

      {/* Bottom Bar - single full-width divider */}
      <div className="relative w-full border-t border-gray-200">
        <div className="px-4 sm:px-6 xl:px-4 xl:mx-[16.67%] py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-xs text-gray-500 order-2 sm:order-1">
              © {new Date().getFullYear()} Replenico, Ltd. All rights reserved.
            </p>
            <div className="flex items-center gap-6 order-1 sm:order-2">
              <Link
                href="/legal/privacy"
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Privacy
              </Link>
              <Link
                href="/legal/terms"
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Terms
              </Link>
              <Link
                href="/legal/cookies"
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
